import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { Children, isValidElement, useCallback, useEffect, useState } from "react";

function extractText(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (isValidElement(node)) return extractText(node.props.children);
  if (Array.isArray(node)) return Children.map(node, extractText)?.join("") ?? "";
  return "";
}

export function CopyableCodeBlock({
  children,
  ...props
}: React.HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const handleCopy = useCallback(() => {
    const text = extractText(children).trim();
    navigator.clipboard.writeText(text).then(() => setCopied(true));
  }, [children]);

  return (
    <div className="relative mb-4">
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 opacity-50 hover:opacity-100 transition-opacity"
      >
        {copied ? (
          <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-400" />
        ) : (
          <ClipboardDocumentIcon className="h-4 w-4" />
        )}
      </button>
      <pre
        className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm"
        {...props}
      >
        {children}
      </pre>
    </div>
  );
}
